import { animate, state, style, transition, trigger } from '@angular/animations';
import { CdkPortalOutlet } from '@angular/cdk/portal';
import { Component, Inject, InjectionToken, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatSidenav } from '@angular/material/sidenav';
import { Title } from '@angular/platform-browser';
import {
  ActivatedRoute,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router
} from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { Actions, ofActionSuccessful, Select, Store } from '@ngxs/store';
import * as Sentry from '@sentry/angular-ivy';
import { BehaviorSubject, combineLatest, fromEvent, Observable, of, timer } from 'rxjs';
import {
  debounce,
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  pairwise,
  shareReplay,
  startWith,
  take,
  withLatestFrom
} from 'rxjs/operators';
import { NavSidebarComponent } from 'src/app/components/nav-sidebar/nav-sidebar.component';
import { GracePeriodBanner } from 'src/app/components/top-banner/banners/grace-period-banner';
import { GrowthBookService } from 'src/app/services/growthbook/service';
import { SegmentService } from 'src/app/services/segment/segment.service';
import { AuthSelectors } from 'src/app/store/auth/auth.selectors';
import { DismissWelcomeBanner } from 'src/app/store/onboarding/onboarding.actions';
import { AuthCompany, AuthUser, PricingPlanCategory } from 'src/models';
import { isCompanyBigForExecutive } from '../common/menu-def.provider';
import { StarterSidebarChangeEvent } from '../components/starter-sidebar/starter-sidebar.component';
import { InviteWebinarBanner } from '../components/top-banner/banners/invite-webinar-banner';
import { RetryPaymentBanner } from '../components/top-banner/banners/retry-payment-banner';
import { SocketHandler } from '../initializers/socket-handler.init';
import {
  isNavatticTutorialMissingTourUrl,
  NavatticExperimentData,
  NavatticTutorial
} from '../integrations/navattic-plugin';
import { ExecutiveDashboardVisibilityService } from '../pages/dashboard/dashboard-executive/services/executive-dashboard-visibility.service';
import { NewBillingState } from '../pages/new-billing/store/state';
import { BillingApiData } from '../pages/new-billing/types/api.types';
import { AMPLITUDE_GUIDE_FLAG, AMPLITUDE_TRACKING_FLAG, AmplitudeService, isAmplitudeCompanyIdentityUnchanged } from '../services/amplitude.service';
import { BookCallReadyService } from '../services/amplitude/book-call-ready.service';
import { PainpointGuideTrigger } from '../services/amplitude/painpoint-guide.trigger';
import { LOGIN_NEW_URL, LOGIN_URL } from '../services/app.constants';
import { BrandingService } from '../services/branding.service';
import { NavatticExperimentService } from '../services/navattic-experiment.service';
import { provideAmpTrackCtxFromUrl } from '../services/tracking/amp-track';
import { AMPLITUDE_DEV_LOGGER_FLAG, installAmpDevLogger } from '../services/tracking/amplitude-dev-logger';
import { TrialOfferDeepLinkService } from '../services/trial-offer/trial-offer-deep-link.service';
import { UrlService } from '../services/url.service';
import { sanitizeAnalyticsUrl } from '../util/analytics-url';
import { NGXS_AUTH_COMPANY_STORAGE_KEY } from '../constants/auth-storage.constants';
import { isUpsellSetupFlowEnabled } from '../constants/upsell-setup-flow.constants';
import { INVITE_SESSION_CLEARED_KEY } from '../services/invite-session/invite-session.constants';
import { AfterLogout, InvalidateUser, Logout, SyncCompanyFromPeerTab } from '../store/auth/auth.actions';
import { AuthState } from '../store/auth/auth.state';
import { PopLoading, PushLoading } from '../store/loading/loading.actions';
import { LoadingState } from '../store/loading/loading.state';
import { ManagerState } from '../store/manager/manager.state';
import { ProgressState } from '../store/progress/progress.state';
import { defualtUserApiLimit } from './app.constants';

export const APP_HEADER_PORTAL = new InjectionToken<CdkPortalOutlet>('App Header Portal', { providedIn: 'root', factory: () => null });
export interface InAppVisitReferrer {
  'type': string;
  'name': string;
  'path': string;
  'url': string;
}

@UntilDestroy()
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  animations: [
    trigger('loading', [
      state('void', style({
        opacity: 0,
      })),
      transition(':enter', [
        animate('0.5s ease-in-out'),
      ]),
      transition(':leave', [
        animate('0.1s ease-in-out'),
      ]),
    ]),
  ],
  providers: [
    {
      provide: APP_HEADER_PORTAL, deps: [AppComponent],
      useFactory: (app: AppComponent) => app.headerPortalOutlet,
    },
    // App-shell fallback page context so sub-nav controls (date/timezone/
    // user-group selectors) resolve data-amp-track-context on every page,
    // not just pages that declare their own provideAmpTrackCtxFromUrl().
    provideAmpTrackCtxFromUrl(),
  ],
})
export class AppComponent implements OnInit, OnDestroy {
  prevInAppVisitReferrer: InAppVisitReferrer;
  activeInAppVisitReferrer: InAppVisitReferrer;
  prevPageName: string;
  activeUrl: string;
  headerHidden$: Observable<boolean>;
  navatticExperiment$: Observable<NavatticExperimentData | null>;
  sidebarVisible$: Observable<boolean>;
  isDemoDialogOpen = false;

  private ampDevLoggerTeardown: (() => void) | null = null;
  private navatticEscKeyHandler: ((event: KeyboardEvent) => boolean) | null = null;
  private navatticClickHandler: ((event: MouseEvent) => boolean) | null = null;
  private navatticModalObserver: MutationObserver | null = null;
  private navatticObserverSetupTimeout: any = null;

  @Select(AuthSelectors.company) company$: Observable<AuthCompany>;
  @Select(AuthState.user) user$: Observable<AuthUser>;
  @Select(NewBillingState.billingData) billingData$: Observable<BillingApiData>;
  @Select(AuthState.isOwnerOrAdmin) isOwnerOrAdmin$: Observable<boolean>;
  @Select(AuthState.authorized) isAuthorized$: Observable<boolean>;
  @Select(AuthState.pricingPlanCategory) pricingPlanCategory$: Observable<PricingPlanCategory>;
  @Select(LoadingState.loading) loading$: Observable<boolean>;
  loadingDebounced$: Observable<boolean>;

  @Select(ProgressState.show) showProgress$: Observable<boolean>;
  @Select(ProgressState.indeterminate) progressIndeterminate$: Observable<boolean>;
  @Select(ProgressState.percentage) progressPercentage$: Observable<number>;

  @ViewChild('headerPortalOutlet', { static: true, read: CdkPortalOutlet }) headerPortalOutlet: CdkPortalOutlet;
  @ViewChild('sidenav', { static: true }) sidenav: MatSidenav;
  @ViewChild('navSidebar', { static: false }) navSidebar: NavSidebarComponent;
  bannerParams = { email: 'support@timedoctor.com' };
  isOwner = false;
  allCompleted = false;
  welcomeSidebarCompleted = false;
  isCompanyBigForExecutive: boolean;
  private embedUrl = '';

  variationId: string | null = null;

  floatingTrialWidgetHeight = 40;
  isDragging = false;

  showThemeChangeModal$ = new BehaviorSubject<boolean>(false);

  constructor(
    private readonly actions: Actions,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly store: Store,
    private readonly title: Title,
    private readonly translate: TranslateService,
    private readonly branding: BrandingService,
    private readonly route: ActivatedRoute,
    private readonly socketHandler: SocketHandler,
    private readonly urlService: UrlService,
    private readonly segment: SegmentService,
    private readonly growthBook: GrowthBookService,
    public gracePeriodBanner: GracePeriodBanner,
    public retryPaymentBanner: RetryPaymentBanner,
    public inviteWebinarBanner: InviteWebinarBanner,
    @Inject(LOGIN_URL) private loginUrl: string,
    @Inject(LOGIN_NEW_URL) private loginNewUrl: string,
    private readonly amplitudeService: AmplitudeService,
    painpointGuides: PainpointGuideTrigger,
    public navatticExperimentService: NavatticExperimentService,
    private readonly executiveVisibility: ExecutiveDashboardVisibilityService,
    private readonly bookCallReady: BookCallReadyService,
    private readonly trialOfferDeepLink: TrialOfferDeepLinkService,
  ) {
    painpointGuides.start();

    this.route.queryParamMap.subscribe(query => {
      this.socketHandler.liveUpdatesEnabled = !query.get('disableLiveUpdates') || query.get('disableLiveUpdates') === 'false';
    });

    // Reactively gate Amplitude on the GrowthBook flag. Defaults to false, so
    // private clouds (no GrowthBook key) never initialize tracking, and toggling
    // the flag pauses/resumes tracking without a reload.
    const analyticsAllowed$ = this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      startWith(null),
      map(() => {
        let activeRoute = this.activatedRoute;
        while (activeRoute.firstChild) {
          activeRoute = activeRoute.firstChild;
        }

        return !activeRoute.snapshot.data['skipAnalytics'];
      }),
      distinctUntilChanged(),
    );

    combineLatest([
      this.growthBook.getFeature<boolean>(AMPLITUDE_TRACKING_FLAG, false),
      this.growthBook.getFeature<boolean>(AMPLITUDE_GUIDE_FLAG, false),
      analyticsAllowed$,
    ]).pipe(
      untilDestroyed(this),
    ).subscribe(([trackingEnabled, guidesEnabled, allowed]) => {
      // Set the guide flag first so the first SDK boot can skip the engagement chunk.
      this.amplitudeService.setGuidesEnabled(guidesEnabled === true);
      this.amplitudeService.setTrackingEnabled(trackingEnabled && allowed);
    });

    // The QA console logger is driven solely by the GrowthBook flag and reacts
    // to changes live, so toggling it off/on takes effect without a reload.
    this.growthBook.getFeature<boolean>(AMPLITUDE_DEV_LOGGER_FLAG, false).pipe(
      untilDestroyed(this),
    ).subscribe(enabled => {
      if (enabled && !this.ampDevLoggerTeardown) {
        this.ampDevLoggerTeardown = installAmpDevLogger();
      } else if (!enabled && this.ampDevLoggerTeardown) {
        this.ampDevLoggerTeardown();
        this.ampDevLoggerTeardown = null;
      }
    });

    combineLatest([
      this.amplitudeService.isReady$.pipe(distinctUntilChanged()),
      this.company$.pipe(distinctUntilChanged(isAmplitudeCompanyIdentityUnchanged)),
      this.user$.pipe(distinctUntilChanged((a, b) => a?.id === b?.id)),
      this.billingData$.pipe(distinctUntilChanged()),
      this.store.select(ManagerState.users).pipe(
        distinctUntilChanged((a, b) =>
          JSON.stringify(a?.filter(u => u.lastTrack).map(u => ({ id: u.id, lastTrack: u.lastTrack }))) ===
          JSON.stringify(b?.filter(u => u.lastTrack).map(u => ({ id: u.id, lastTrack: u.lastTrack }))),
        ),
      ),
      this.store.select(AuthState.hasBillingAccess).pipe(distinctUntilChanged()),
    ]).pipe(untilDestroyed(this),
    ).subscribe(([isReady, company, user, billingData, manageUsers, hasBillingAccess]) => {
      this.amplitudeService.identify(isReady, user, company, billingData?.billingDetails, manageUsers, hasBillingAccess);
    });

    // Milestones depend only on onboarding completion and Amplitude readiness.
    // A dedicated subscription keeps the timers stable while identify inputs update.
    combineLatest([
      this.amplitudeService.isReady$.pipe(distinctUntilChanged()),
      this.company$.pipe(distinctUntilChanged((previous, current) =>
        previous?.id === current?.id
        && previous?.companySettings?.custom?.completedOnBoardingAt
          === current?.companySettings?.custom?.completedOnBoardingAt,
      )),
    ]).pipe(
      untilDestroyed(this),
    ).subscribe(([, company]) => {
      this.bookCallReady.track(company);
    });

    combineLatest([this.isAuthorized$, this.growthBook.getFeature('new-theme-announcement-modal')]).pipe(
      map(([isAuthorized, isFeatureEnabled]) => {
        return isAuthorized && isFeatureEnabled && !localStorage.getItem('viewedThemeChangeModal');
      }),
      distinctUntilChanged(),
      untilDestroyed(this),
    ).subscribe(showThemeChangeModal => {
      this.showThemeChangeModal$.next(showThemeChangeModal);
    });

    this.navatticExperiment$ = this.navatticExperimentService.experiment$.pipe(
      untilDestroyed(this),
    );

    const showUpsell$ = this.executiveVisibility.showUpsell$.pipe(
      startWith(false),
    );

    const executiveRoute$ = this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(event => event.urlAfterRedirects),
      startWith(this.router.url),
      map(url => {
        const path = (url ?? '').split('?')[0];
        return path === '/dashboard-executive' || path.startsWith('/dashboard-executive/');
      }),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    const executiveUpsellVisible$ = combineLatest([
      executiveRoute$,
      showUpsell$,
    ]).pipe(
      map(([isExecutiveRoute, showUpsell]) => isExecutiveRoute && showUpsell),
      startWith(false),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.sidebarVisible$ = combineLatest([
      this.navatticExperimentService.isVariant$('B'),
      executiveUpsellVisible$,
    ]).pipe(
      map(([isVariant, executiveUpsellVisible]) => {
        return !isVariant && !executiveUpsellVisible;
      }),
      distinctUntilChanged(),
      untilDestroyed(this),
    );

  }

  ngOnInit(): void {
    this.trialOfferDeepLink.init();

    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      startWith(0),
      untilDestroyed(this),
    ).subscribe(() => {
      let child = this.router.routerState.snapshot.root;
      while (child.firstChild) {
        child = child.firstChild;
      }
      const paths = child.pathFromRoot.map(x => x?.routeConfig?.path || '').filter(x => x);
      this.activeUrl = paths.join('/');
    });

    const routeData = this.router.events.pipe(
      untilDestroyed(this),
      filter((event) => event instanceof NavigationEnd),
      map(() => this.activatedRoute),
      map((route) => {
        while (route.firstChild) {
          route = route.firstChild;
        }
        return route;
      }),
      filter((route) => route.outlet === 'primary'),
      mergeMap((route) => route.data),
    );

    combineLatest([
      this.store.select(AuthState.company).pipe(distinctUntilChanged(), untilDestroyed(this)),
      routeData.pipe(
        map(data => ({
          title: data.title ? this.translate.instant(data.title) : this.branding.getAppName(),
          skipAnalytics: !!data.skipAnalytics,
        })),
      ),
    ]).pipe(
      untilDestroyed(this),
    ).subscribe(([company, { title, skipAnalytics }]) => {
      let titleText = title;
      if (company) {
        titleText = `${company.name} - ${titleText}`;
        this.socketHandler.liveUpdatesEnabled = company.userCount <= defualtUserApiLimit;
      }
      this.title.setTitle(titleText);
      this.prevInAppVisitReferrer = this.activeInAppVisitReferrer;
      this.isCompanyBigForExecutive = isCompanyBigForExecutive(company);
      if (this.activeUrl !== '') {
        this.activeInAppVisitReferrer = {
          type: 'webapp page',
          name: titleText,
          path: '/' + this.activeUrl,
          url: sanitizeAnalyticsUrl(window.location.href),
        };
        this.urlService.setPreviousUrl(this.activeInAppVisitReferrer);
        const { category, pageName, ...props } = this.urlService.getPageNameByPath(this.activeUrl, title);
        if (title !== 'Onboarding' && this.prevPageName !== pageName && !skipAnalytics) {
          this.prevPageName = pageName;
          this.segment.page(category, pageName, props, this.prevInAppVisitReferrer);
        }
      }
    });

    this.headerHidden$ = routeData.pipe(map((data) => !!data.hideHeader));

    fromEvent<StorageEvent>(window, 'storage')
      .pipe(
        filter((event) => event.key === INVITE_SESSION_CLEARED_KEY && !!event.newValue),
        untilDestroyed(this),
      )
      .subscribe(() => {
        if (!this.store.selectSnapshot(AuthState.authorized)) {
          return;
        }
        const [path] = this.router.url.split('?');
        if (path.startsWith('/join') || path.startsWith('/activate')) {
          return;
        }
        this.store.dispatch(new InvalidateUser());
        this.router.navigateByUrl(this.loginUrl);
      });

    fromEvent<StorageEvent>(window, 'storage')
      .pipe(
        filter((event) => event.key === NGXS_AUTH_COMPANY_STORAGE_KEY && !!event.newValue),
        untilDestroyed(this),
      )
      .subscribe((event) => {
        if (!isUpsellSetupFlowEnabled(this.growthBook)) {
          return;
        }
        if (!this.store.selectSnapshot(AuthState.authorized)) {
          return;
        }
        try {
          const newValue = event.newValue;
          if (!newValue) {
            return;
          }
          const company = JSON.parse(newValue);
          const current = this.store.selectSnapshot(AuthState.company);
          if (!company?.id || company.id !== current?.id) {
            return;
          }
          if (
            current?.pricingPlan === company.pricingPlan
            && current?.companySettings?.workScheduleFeature === company.companySettings?.workScheduleFeature
            && current?.companySettings?.webAndAppTracking === company.companySettings?.webAndAppTracking
          ) {
            return;
          }
          this.store.dispatch(new SyncCompanyFromPeerTab(company));
        } catch {
          // Ignore malformed peer-tab storage payloads.
        }
      });

    this.store
      .select(AuthState.company)
      .pipe(
        map((x) => x?.id),
        distinctUntilChanged(),
        pairwise(),
        withLatestFrom(routeData.pipe(map((data) => data.onCompanyChange))),
        untilDestroyed(this),
      )
      .subscribe(([[prev, cur], onChange]) => {
        if (onChange === 'ignore' || !prev || !cur) {
          return;
        }
        if (onChange === 'rerun') {
          this.router.navigateByUrl(this.router.url);
        } else {
          window.location.pathname = '/';
        }
      });

    this.actions.pipe(ofActionSuccessful(Logout), untilDestroyed(this)).subscribe(async (action: Logout) => {
      await this.router.navigateByUrl(action.errorMessage ? this.loginNewUrl : this.loginUrl);
      this.store.dispatch(new AfterLogout());
    });

    this.router.events.pipe(
      filter((x) => x instanceof NavigationStart),
      untilDestroyed(this),
    ).subscribe(() => {
      this.store.dispatch(new PushLoading());
      this.sidenav.close();
      // Clean up Navattic event listeners on navigation
      this.cleanupEscKeyHandler();
    });

    this.router.events.pipe(
      filter((x) => x instanceof NavigationEnd || x instanceof NavigationCancel || x instanceof NavigationError),
      untilDestroyed(this),
    ).subscribe(() => {
      this.store.dispatch(new PopLoading());
    });

    // Show the master loading in index.html until the first navigation occurs
    // Also hide the other loading so that there are not two loadings
    const firstLoad = this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      take(1),
    );
    firstLoad.subscribe(() => {
      const masterLoading = document.querySelector('#master-loading');
      if (masterLoading?.parentElement) {
        masterLoading.parentElement.removeChild(masterLoading);
      }
    });

    this.loadingDebounced$ = combineLatest([
      firstLoad.pipe(
        map(() => true),
        startWith(false),
      ),
      this.loading$,
    ]).pipe(
      map(([x, y]) => x && y),
      distinctUntilChanged(),
      debounce((x) => timer(x ? 300 : 0)),
    );

    this.store.select(AuthState.company).pipe(
      distinctUntilChanged((a, b) => a?.id === b?.id),
      filter(x => !!x),
    ).subscribe(company => {
      this.setupDataLayer(company);
    });

    this.checkIframe();

    // Listen for CustomEvent dispatched by NavatticActionsService.startTutorial()
    // This handles "Take a Tour" clicks from the menu
    fromEvent<CustomEvent>(window, 'navattic-start-tutorial')
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.startNavatticTutorial();
      });

  }

  ngOnDestroy(): void {
    this.bookCallReady.clear();
    // Ensure all Navattic event listeners are properly cleaned up
    this.cleanupEscKeyHandler();
    this.ampDevLoggerTeardown?.();
    this.ampDevLoggerTeardown = null;
  }

  setupDataLayer(company: AuthCompany) {
    this.store.select(AuthState.user).pipe(
      distinctUntilChanged((a, b) => a?.id === b?.id),
      filter(x => !!x),
    ).subscribe(user => {
      if (!(window?.dataLayer && company && user)) { return; }
      const isPaid = company?.subscription?.status === 'paid' || false;
      const arrName = user.name.split(' ');
      let userName = user.name;
      let lastName = '';
      if (arrName.length > 1) {
        userName = arrName[0];
        delete arrName[0];
        lastName = arrName.join(' ').trim();
      }
      const props = {
        userEmail: user.email,
        companyID: company.id,
        userID: user.id,
        userName,
        lastName,
        companyName: company.name,
        siteType: window.location.hostname,
        isPaid,
        userLevel: company?.role,
        userPlan: company?.pricingPlan,
        userCount: company?.userCount,
        companyRegDate: company?.companyCreatedAt,
        ipAddress: user?.lastSeenGlobal?.ip,
      };
      window.dataLayer.push(props);
    });
  }

  dismissBanner() {
    this.store.dispatch(new DismissWelcomeBanner());
  }

  onChangeStarterSideBar(event: StarterSidebarChangeEvent) {
    this.isOwner = event.isOwner;
    this.allCompleted = event.allCompleted;
    this.welcomeSidebarCompleted = event.welcomeSidebarCompleted;
  }

  checkIframe() {
    if (window.location !== window.parent.location) {
      Sentry.captureMessage('Loaded inside iframe', {
        level: 'info',
        tags: { iframe: true },
        extra: {
          href: window.location.href,
          parentHref: document.referrer || '',
        },
      });
    }
  }

  closeThemeChangeModal() {
    this.showThemeChangeModal$.next(false);
    localStorage.setItem('viewedThemeChangeModal', 'true');
  }

  isWidgetDragged(dragging: boolean) {
    this.isDragging = dragging;
  }

  initializeNavatticPopup() {
    // Use take(1) to ensure we only open once per explicit call
    // Also check if tutorial is already completed - don't auto-open if completed
    const tutorial$ = this.navatticExperimentService?.getActiveTutorial$('B') || of(null);
    tutorial$
      .pipe(
        take(1), // Only take the first emission to prevent auto-opening on subsequent emissions
        untilDestroyed(this),
      )
      .subscribe(async (tutorial) => {
        if (!tutorial?.tourUrl) {
          // Reset dialog state since we're not opening
          this.isDemoDialogOpen = false;
          // Cancel pending observer setup timeout
          if (this.navatticObserverSetupTimeout) {
            clearTimeout(this.navatticObserverSetupTimeout);
            this.navatticObserverSetupTimeout = null;
          }
          // Clean up observer if it was set up in onDemoDialogOpened()
          this.cleanupEscKeyHandler();
          if (isNavatticTutorialMissingTourUrl(tutorial)) {
            Sentry.captureMessage(
              '[Navattic] Matched tutorial missing tourUrl',
              {
                level: 'error',
                extra: {
                  tutorial,
                },
              },
            );
          }
          return;
        }

        const userData =
          this.navatticExperimentService.getUserIdentificationData();
        const tourUrlWithUserData = this.addUserDataToUrl(
          tutorial.tourUrl,
          userData,
        );

        this.trackTourEvent(
          'Navattic Tour Event Initiated',
          'initiate',
          tutorial.title || '',
          tutorial.tourUrl,
        );

        // Store the embed URL for the hideTitleBlock method
        this.embedUrl = tourUrlWithUserData;

        const config = this.getNavatticConfig(tourUrlWithUserData, tutorial);

        // Check if NavatticEmbed is available
        if (!(window as any).NavatticEmbed) {
          // Reset dialog state since we can't open
          this.isDemoDialogOpen = false;
          // Cancel pending observer setup timeout
          if (this.navatticObserverSetupTimeout) {
            clearTimeout(this.navatticObserverSetupTimeout);
            this.navatticObserverSetupTimeout = null;
          }
          // Clean up observer if it was set up in onDemoDialogOpened()
          this.cleanupEscKeyHandler();
          return;
        }

        // Only set isDemoDialogOpen = true AFTER we confirm we can actually open the tutorial
        this.isDemoDialogOpen = true;

        (window as any).NavatticEmbed.initPopup(tourUrlWithUserData, config);

        this.preventEscKeyClosing();
        this.preventBackdropClickClosing();

        (window as any).NavatticEmbed.openPopup(tourUrlWithUserData);

        // Setup z-index and observer when tutorial opens from menu (FAB path triggers onDemoDialogOpened via event)
        this.onDemoDialogOpened();

        // Hide the title block using Navattic API and Shadow DOM
        setTimeout(() => {
          this.hideTitleBlock();
        }, 100);
      });
  }

  onTrialWidgetHeightChanged(height: number) {
    this.floatingTrialWidgetHeight = height;
  }

  onDemoDialogOpened(): void {
    // Don't set isDemoDialogOpen here - it will be set in initializeNavatticPopup() only if tutorial actually opens
    // This prevents the backdrop from showing when tutorial is already completed or can't open

    // Cancel any pending observer setup timeout
    if (this.navatticObserverSetupTimeout) {
      clearTimeout(this.navatticObserverSetupTimeout);
      this.navatticObserverSetupTimeout = null;
    }

    // Ensure Navattic modal has proper z-index when opened
    // Only set up observer if dialog actually opens (checked in setTimeout)
    this.navatticObserverSetupTimeout = setTimeout(() => {
      // Only set up observer if tutorial actually opened
      if (this.isDemoDialogOpen) {
        this.ensureNavatticModalZIndex();
        this.setupNavatticModalObserver();
      }
      this.navatticObserverSetupTimeout = null;
    }, 100);
  }

  onDemoDialogClosed(): void {
    this.isDemoDialogOpen = false;

    // Cancel pending observer setup timeout
    if (this.navatticObserverSetupTimeout) {
      clearTimeout(this.navatticObserverSetupTimeout);
      this.navatticObserverSetupTimeout = null;
    }

    // Clean up all Navattic event listeners and observers when dialog is closed
    this.cleanupEscKeyHandler();
  }

  onOverlayClick(event: MouseEvent): void {
    // Prevent clicks on the overlay from reaching other elements
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  }

  private ensureNavatticModalZIndex(): void {
    // Find and ensure Navattic modal elements have proper z-index
    const modalSelectors = [
      'navattic-lightbox',
      '[id="modal"]',
      '.navattic-lightbox',
      '.navattic-modal',
      '.navattic-dialog',
    ];

    const closeButtonSelectors = [
      '[id="close-demo"]',
      '#close-demo',
      'button[id="close-demo"]',
      '.navattic-close-button',
      '.navattic-modal-close',
    ];

    modalSelectors.forEach((selector) => {
      const elements = document.querySelectorAll(selector);
      elements.forEach((element: HTMLElement) => {
        element.style.zIndex = '2147483500';
        element.style.position = 'fixed';
      });
    });

    closeButtonSelectors.forEach((selector) => {
      const elements = document.querySelectorAll(selector);
      elements.forEach((element: HTMLElement) => {
        element.style.zIndex = '2147483501';
        element.style.position = 'relative';
        element.style.pointerEvents = 'auto';
      });
    });

    // Also ensure any Navattic content containers
    const contentSelectors = [
      '.navattic-modal-content',
      '.navattic-dialog-content',
      '[class*="navattic"]',
    ];

    contentSelectors.forEach((selector) => {
      const elements = document.querySelectorAll(selector);
      elements.forEach((element: HTMLElement) => {
        element.style.zIndex = '2147483500';
      });
    });
  }

  private setupNavatticModalObserver(): void {
    // Clean up existing observer
    if (this.navatticModalObserver) {
      this.navatticModalObserver.disconnect();
    }

    // Set up observer to watch for Navattic modal elements being added
    this.navatticModalObserver = new MutationObserver((mutations) => {
      let shouldUpdate = false;

      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as HTMLElement;
              // Check if it's a Navattic-related element or AnnounceKit banner
              if (
                element.tagName === 'NAVATTIC-LIGHTBOX' ||
                element.id === 'modal' ||
                element.classList.toString().includes('navattic') ||
                element.classList
                  .toString()
                  .includes('announcekit-booster-bar') ||
                element.querySelector('[id="close-demo"]') ||
                element.querySelector('#close-demo')
              ) {
                shouldUpdate = true;
              }
            }
          });
        }
      });

      if (shouldUpdate) {
        // Small delay to ensure elements are fully rendered
        setTimeout(() => {
          this.ensureNavatticModalZIndex();
        }, 50);
      }
    });

    // Start observing the document body for changes
    this.navatticModalObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class'],
    });
  }


  private hideTitleBlock(): void {
    const lightbox = (window as any).NavatticEmbed?.getLightboxByUrl(
      this.embedUrl,
    );
    if (!lightbox) return;

    const apply = () => {
      const sr = lightbox.shadowRoot;
      if (!sr) return;

      // Hide the left-hand block
      const titleBlock = sr.querySelector(
        '.flex.items-center.gap-x-2.truncate.text-sm.font-medium',
      );
      if (titleBlock) {
        (titleBlock as HTMLElement).style.display = 'none';
      }

      // Fix alignment so the close button stays right-aligned
      const header = sr.querySelector(
        '.flex.h-\\[50px\\].w-full.items-center.justify-between',
      );
      if (header) {
        (header as HTMLElement).style.justifyContent = 'flex-end'; // override Tailwind justify-between
      }
    };

    apply();
    const observer = new MutationObserver(apply);
    observer.observe(lightbox, { subtree: true, childList: true });
  }

  startNavatticTutorial(): void {
    this.initializeNavatticPopup();
  }

  private addUserDataToUrl(
    tourUrl: string,
    userData: Record<string, any> | null,
  ): string {
    if (!userData) {
      return tourUrl;
    }

    try {
      const url = new URL(tourUrl);

      // Add user data as query parameters
      Object.entries(userData).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          url.searchParams.set(key, String(value));
        }
      });

      const finalUrl = url.toString();
      return finalUrl;
    } catch (error) {
      return tourUrl;
    }
  }

  private getNavatticConfig(fullUrl: string, tutorial?: NavatticTutorial) {
    const title = tutorial?.title || tutorial?.slug || '';
    this.trackTourEvent('Navattic Tour Event Started', 'start', title, fullUrl);

    return {
      title,
      preload: true,

      onClose: () => {
        this.trackTourEvent(
          'Navattic Tour Event Closed',
          'dismiss',
          title,
          fullUrl,
        );
        this.onDemoDialogClosed();
        this.cleanupEscKeyHandler();
      },
      onComplete: () => {
        this.trackTourEvent(
          'Navattic Tour Event Completed',
          'complete',
          title,
          fullUrl,
        );
      },
    };
  }

  private preventEscKeyClosing(): void {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        return false;
      }
    };

    try {
      document.addEventListener('keydown', handleKeyDown, true);
      this.navatticEscKeyHandler = handleKeyDown;
    } catch (error) {
      Sentry.captureException(error, {
        tags: { component: 'AppComponent', method: 'preventEscKeyClosing' },
        extra: { message: '[Navattic] Failed to add keydown event listener' },
      });
    }
  }

  private preventBackdropClickClosing(): void {
    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // Check if click is on a close button or within a close button area
      const isCloseButton = this.isCloseButtonClick(target, event);

      if (isCloseButton) {
        return true;
      }

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      return false;
    };

    try {
      document.addEventListener('click', handleClick, true);
      this.navatticClickHandler = handleClick;
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          component: 'AppComponent',
          method: 'preventBackdropClickClosing',
        },
        extra: { message: '[Navattic] Failed to add click event listener' },
      });
    }
  }

  private isCloseButtonClick(target: HTMLElement, event: MouseEvent): boolean {
    // Check if the clicked element or any of its parents is a close button
    const closeButtonSelectors = [
      '[id="close-demo"]',
      '#close-demo',
      'button[id="close-demo"]',
      '.navattic-close-button',
      '.navattic-modal-close',
      '[class*="close"]',
      '[class*="Close"]',
    ];

    // Check the target element and all its parents
    let element: HTMLElement | null = target;
    while (element) {
      // Check by ID
      if (element.id === 'close-demo') {
        return true;
      }

      // Check by class names
      const classList = element.classList;
      if (
        classList.contains('navattic-close-button') ||
        classList.contains('navattic-modal-close') ||
        Array.from(classList).some((cls) => cls.toLowerCase().includes('close'))
      ) {
        return true;
      }

      // Check by CSS selectors
      for (const selector of closeButtonSelectors) {
        if (element.matches && element.matches(selector)) {
          return true;
        }
      }

      element = element.parentElement;
    }

    // Fallback: Check if click is in the adjusted zone (20% of original + 20px, positioned 10% from right, 8% from top)
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
    const zoneWidth = 45; // Fixed 45px width
    const zoneHeight = 45; // Fixed 45px height
    const rightOffset = screenWidth * 0.04; // 4% from right edge
    const topOffset = screenHeight * 0.04; // 4% from top edge

    return (
      event.clientX >= screenWidth - rightOffset - zoneWidth &&
      event.clientX <= screenWidth - rightOffset &&
      event.clientY >= topOffset &&
      event.clientY <= topOffset + zoneHeight
    );
  }

  private cleanupEscKeyHandler(): void {
    // Clean up keydown event listener
    if (this.navatticEscKeyHandler) {
      try {
        document.removeEventListener(
          'keydown',
          this.navatticEscKeyHandler,
          true,
        );
      } catch (error) {
        Sentry.captureException(error, {
          tags: { component: 'AppComponent', method: 'cleanupEscKeyHandler' },
          extra: {
            message: '[Navattic] Failed to remove keydown event listener',
          },
        });
      } finally {
        this.navatticEscKeyHandler = null;
      }
    }

    // Clean up click event listener
    if (this.navatticClickHandler) {
      try {
        document.removeEventListener('click', this.navatticClickHandler, true);
      } catch (error) {
        Sentry.captureException(error, {
          tags: { component: 'AppComponent', method: 'cleanupEscKeyHandler' },
          extra: {
            message: '[Navattic] Failed to remove click event listener',
          },
        });
      } finally {
        this.navatticClickHandler = null;
      }
    }

    // Clean up mutation observer
    if (this.navatticModalObserver) {
      try {
        this.navatticModalObserver.disconnect();
      } catch (error) {
        Sentry.captureException(error, {
          tags: { component: 'AppComponent', method: 'cleanupEscKeyHandler' },
          extra: {
            message: '[Navattic] Failed to disconnect mutation observer',
          },
        });
      } finally {
        this.navatticModalObserver = null;
      }
    }
  }

  private trackTourEvent(
    eventTitle,
    event: 'start' | 'complete' | 'dismiss' | 'initiate',
    tourTitle: string,
    tourUrl?: string,
  ) {
    this.company$.pipe(take(1)).subscribe((company) => {
      this.store
        .select(AuthState.user)
        .pipe(take(1))
        .subscribe((user) => {
          this.segment.trackInAppEvents(eventTitle, {
            event,
            tourUrl,
            timestamp: new Date().toISOString(),
            tourTitle,
            userFullName: user.name,
            appSubscription: company.subscription?.status,
          });
        });
    });
  }
}
